// src/pages/EditShow.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '../firebase';
import {
  collection,
  collectionGroup,
  getDocs,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  increment,
  runTransaction,
  query,
  where
} from 'firebase/firestore';
import { BONUS_LIST } from '../components/BonusList';
import { TEAM_COLORS } from '../data/colors';
import CHAMPIONSHIPS from '../data/championships';
import { getBonusPointsForShow, getShowBonusCategory, getStoredBonusDisplay, isBonusAvailableForShow } from '../utils/bonusConfig';
import ProtectedRoute from '../components/ProtectedRoute';
import ParticipationForm from '../components/show/ParticipationForm';
import ParticipationManager from '../components/show/ParticipationManager';
import MatchForm from '../components/show/MatchForm';
import MatchManager from '../components/show/MatchManager';
import '../styles/ShowEditor.css';
import {
  applyParticipationDeltaToFactionScores,
  getShowScoringDate
} from '../utils/factionLineupScoring';
import { buildChangeDescription, recordAdminUpdate } from '../utils/adminUpdates';
import {
  getMatchTitleLabel,
  getMatchTitleValues,
  isTitleMatch as isStoredTitleMatch,
  normalizeMatchTitleValues
} from '../utils/matchTitles';
import {
  buildTitleBonusSuggestionsForWrestler,
  loadTitleBonusSuggestionBundle
} from '../utils/titleBonusSuggestions';

const normalizeStoredBonusPoint = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const point = Number(value);
  return Number.isFinite(point) ? point : null;
};

const buildBonusPointsForRecord = (selectedBonuses) => (
  selectedBonuses.flatMap((bonus) =>
    Array.from({ length: bonus.qty }, () => normalizeStoredBonusPoint(bonus.points))
  )
);

const ShowEditorProgress = ({ label, value, fullPage = false }) => {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  const roundedValue = Math.round(safeValue);

  return (
    <div
      className={`show-editor-loading ${fullPage ? 'show-editor-loading--full' : 'show-editor-loading--inline'}`}
      role="status"
      aria-live="polite"
    >
      <div className="show-editor-loading__meta">
        <span className="show-editor-loading__icon" aria-hidden="true">
          <i className="bi bi-arrow-repeat"></i>
        </span>
        <span className="show-editor-loading__copy">
          <span className="show-editor-loading__label">{label || 'Caricamento dati...'}</span>
          <span className="show-editor-loading__hint">
            {fullPage ? 'Preparazione dati show' : 'Operazione in corso'}
          </span>
        </span>
        <span className="show-editor-loading__percent">{roundedValue}%</span>
      </div>
      <div
        className="show-editor-progress"
        role="progressbar"
        aria-label="Barra di caricamento"
        aria-valuenow={roundedValue}
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span className="show-editor-progress__fill" style={{ width: `${safeValue}%` }} />
      </div>
    </div>
  );
};

const getBonusPointsForRecord = (bonuses = [], bonusPoints = []) => (
  bonuses.map((_, index) => normalizeStoredBonusPoint(bonusPoints[index]))
);

const MATCH_PRESENCE_BONUS_VALUES = new Set(['match_sett', 'match_ple']);
const MATCH_WIN_BONUS_VALUES = new Set(['vince_sett', 'vince_ple']);
const AUTO_PUBLISH_TIMEZONE = 'Europe/Rome';
const LIVE_BONUS_LOCK_MS = 2 * 24 * 60 * 60 * 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const SAME_EVENT_DATE_WINDOW_DAYS = 5;

const normalizeStoredBonusValue = (value) => String(value || '').replace(/_ext$/, '');

const toDateValue = (value) => {
  const date =
    value instanceof Date
      ? value
      : typeof value?.toDate === 'function'
      ? value.toDate()
      : value
      ? new Date(value)
      : null;

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const formatEventDateOptionLabel = (value) => {
  const date = toDateValue(value);
  if (!date) return 'Data non disponibile';

  return date.toLocaleString('it-IT', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const isShowWithinSameEventWindow = (currentDate, candidateDate) => {
  if (!currentDate || !candidateDate) return false;
  return Math.abs(candidateDate.getTime() - currentDate.getTime()) <= SAME_EVENT_DATE_WINDOW_DAYS * DAY_IN_MS;
};

const getMatchAssignedParticipantIds = (matches = []) => new Set(
  matches.flatMap((match) => {
    if (Array.isArray(match.participants)) return match.participants;
    if (Array.isArray(match.teams)) {
      return match.teams.flatMap((team) =>
        Array.isArray(team.participants) ? team.participants : []
      );
    }
    return [];
  }).filter(Boolean)
);

const buildStoredParticipationPayload = (participation) => {
  const { id, wrestlerName, ...payload } = participation || {};

  return {
    ...payload,
    bonuses: Array.isArray(payload.bonuses) ? payload.bonuses : [],
    bonusPoints: Array.isArray(payload.bonusPoints) ? payload.bonusPoints : [],
    totalPoints: Number(payload.totalPoints) || 0,
    timestamp: payload.timestamp || new Date()
  };
};

const getDateTimePartsInTimeZone = (value, timeZone = AUTO_PUBLISH_TIMEZONE) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });
  const parts = formatter.formatToParts(date).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour || 0),
    minute: Number(parts.minute || 0),
    second: Number(parts.second || 0)
  };
};

const getTimeZoneOffsetMs = (date, timeZone = AUTO_PUBLISH_TIMEZONE) => {
  const parts = getDateTimePartsInTimeZone(date, timeZone);
  if (!parts) return 0;

  const utcValue = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );

  return utcValue - date.getTime();
};

const getZonedDate = (parts, timeZone = AUTO_PUBLISH_TIMEZONE) => {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute || 0,
    parts.second || 0
  );
  const firstPass = new Date(utcGuess - getTimeZoneOffsetMs(new Date(utcGuess), timeZone));
  return new Date(utcGuess - getTimeZoneOffsetMs(firstPass, timeZone));
};

const getAutoPublishTargetDate = (value) => {
  const showDateValue = getShowScoringDate({ data: value });
  const showParts = getDateTimePartsInTimeZone(showDateValue);
  if (!showParts) return null;

  return getZonedDate({
    year: showParts.year,
    month: showParts.month,
    day: showParts.day + 1,
    hour: 12,
    minute: 0,
    second: 0
  });
};

const getNextFutureNoonDate = (now = new Date()) => {
  const nowParts = getDateTimePartsInTimeZone(now);
  if (!nowParts) return null;

  const todayNoon = getZonedDate({
    year: nowParts.year,
    month: nowParts.month,
    day: nowParts.day,
    hour: 12,
    minute: 0,
    second: 0
  });

  if (todayNoon.getTime() > now.getTime()) {
    return todayNoon;
  }

  return getZonedDate({
    year: nowParts.year,
    month: nowParts.month,
    day: nowParts.day + 1,
    hour: 12,
    minute: 0,
    second: 0
  });
};

const resolveAutoPublishTargetDate = (
  showValue,
  preferredValue = null,
  now = new Date(),
  { ensureFuture = false } = {}
) => {
  const preferredDate = toDateValue(preferredValue);
  const fallbackDate = getAutoPublishTargetDate(showValue);
  const targetDate = preferredDate || fallbackDate;

  if (!ensureFuture || !targetDate) return targetDate;
  return targetDate.getTime() > now.getTime()
    ? targetDate
    : getNextFutureNoonDate(now);
};

const getCountdownParts = (targetDate, now = new Date()) => {
  if (!(targetDate instanceof Date) || Number.isNaN(targetDate.getTime())) {
    return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0, isReady: false };
  }

  const totalMs = targetDate.getTime() - now.getTime();
  const safeMs = Math.max(0, totalMs);
  const totalSeconds = Math.floor(safeMs / 1000);

  return {
    totalMs,
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    isReady: totalMs <= 0
  };
};

const formatAutoPublishTargetLabel = (value) => {
  const targetDate = toDateValue(value);
  if (!targetDate) return '';

  return targetDate.toLocaleString('it-IT', {
    timeZone: AUTO_PUBLISH_TIMEZONE,
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatTimestampLabel = (value) => {
  if (!value) return '';

  const date =
    value instanceof Date
      ? value
      : typeof value?.toDate === 'function'
      ? value.toDate()
      : new Date(value);

  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getReadableDateTimeLabel = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';

  return value.toLocaleString('it-IT', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

function EditShow() {
  const [showPresets, setShowPresets] = useState(false);
  const { id: showId } = useParams();
  const [showName, setShowName] = useState(null);
  const [showDate, setShowDate] = useState(null);
  const [showData, setShowData] = useState(null);
  const [wrestlers, setWrestlers] = useState([]);
  const [participations, setParticipations] = useState([]);
  const [draftParticipations, setDraftParticipations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [savedTeams, setSavedTeams] = useState([]);
  const [savedGuests, setSavedGuests] = useState([]);
  const [selectedWrestlers, setSelectedWrestlers] = useState([]);
  const [selectedBonuses, setSelectedBonuses] = useState([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [editorNow, setEditorNow] = useState(() => Date.now());
  const [busyCount, setBusyCount] = useState(0);
  const [busyLabel, setBusyLabel] = useState('Caricamento dati...');
  const [busyProgress, setBusyProgress] = useState(0);
  const [publishingDrafts, setPublishingDrafts] = useState(false);
  const publishingDraftsRef = useRef(false);
  const [editingParticipationId, setEditingParticipationId] = useState(null);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [activeTab, setActiveTab] = useState('participations');
  const [participationMode, setParticipationMode] = useState('live');
  const [autoPublishDraftsAtNoon, setAutoPublishDraftsAtNoon] = useState(false);
  const [autoPublishCountdown, setAutoPublishCountdown] = useState(() =>
    getCountdownParts(null)
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedParticipationsForEdit, setSelectedParticipationsForEdit] = useState([]);
  const [sameEventShows, setSameEventShows] = useState([]);
  const [selectedMoveTargetShowId, setSelectedMoveTargetShowId] = useState('');
  const [movingParticipations, setMovingParticipations] = useState(false);
  const [bonusSuggestionsByWrestler, setBonusSuggestionsByWrestler] = useState({});
  const [bonusSuggestionsLoading, setBonusSuggestionsLoading] = useState(false);

  const [matchParticipants, setMatchParticipants] = useState([]);
  const [matchStipulation, setMatchStipulation] = useState('');
  const [matchTitle, setMatchTitle] = useState('');
  const [secondaryMatchTitle, setSecondaryMatchTitle] = useState('');
  const [isTitleMatch, setIsTitleMatch] = useState(false);
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [isMultiTeamMatch, setIsMultiTeamMatch] = useState(false);
  const [teams, setTeams] = useState([{ id: 1, name: 'Squadra A', participants: [], color: 'danger' }]);
  const [guestMap, setGuestMap] = useState({});
  const [winner, setWinner] = useState('');
  const [isDisqualificationWin, setIsDisqualificationWin] = useState(false);

  const clampProgress = (value) => Math.max(0, Math.min(100, value));
  const updateLocalWrestlerScore = (wrestlerId, scoreDelta) => {
    if (!wrestlerId || !Number.isFinite(Number(scoreDelta)) || Number(scoreDelta) === 0) return;

    setWrestlers((prev) =>
      prev.map((wrestler) =>
        wrestler.id === wrestlerId
          ? { ...wrestler, score: (Number(wrestler.score) || 0) + Number(scoreDelta) }
          : wrestler
      )
    );
  };

  const startBusy = (label, initialProgress = 8) => {
    setBusyLabel(label);
    setBusyProgress(clampProgress(initialProgress));
    setBusyCount((prev) => prev + 1);
  };

  const stopBusy = () => {
    setBusyCount((prev) => Math.max(0, prev - 1));
  };

  const runWithBusy = async (label, action, options = {}) => {
    startBusy(label, options.initialProgress);
    try {
      return await action((value) => setBusyProgress(clampProgress(value)));
    } finally {
      setBusyProgress(100);
      stopBusy();
    }
  };

  useEffect(() => {
    if (busyCount === 0) {
      const timeoutId = setTimeout(() => {
        setBusyProgress(0);
        setBusyLabel('Controlli pronti');
      }, 250);

      return () => clearTimeout(timeoutId);
    }
  }, [busyCount]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setEditorNow(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      await runWithBusy('Caricamento show...', async (setProgress) => {
        try {
          setProgress(10);
          const showRef = doc(db, 'shows', showId);
          const showSnap = await getDoc(showRef);

          if (!showSnap.exists()) {
            setMessage('Show non trovato');
            setLoading(false);
            return;
          }
          const data = showSnap.data();
          setShowData(showSnap.data());
          setShowName(data.nome);
          setShowDate(data.data.toDate().toLocaleDateString());
          setAutoPublishDraftsAtNoon(data.autoPublishDraftsAtNoon === true);
          const currentShowDateValue = toDateValue(data.data);

          if (data.nome) {
            const sameNameShowsSnap = await getDocs(
              query(collection(db, 'shows'), where('nome', '==', data.nome))
            );
            const nextSameEventShows = sameNameShowsSnap.docs
              .map((showDoc) => {
                const siblingData = showDoc.data();
                const siblingDateValue = toDateValue(siblingData.data);

                return {
                  id: showDoc.id,
                  nome: siblingData.nome || data.nome,
                  type: siblingData.type || 'weekly',
                  data: siblingData.data,
                  dateValue: siblingDateValue,
                  label: formatEventDateOptionLabel(siblingDateValue)
                };
              })
              .filter((showItem) => (
                showItem.id !== showId &&
                showItem.type === (data.type || 'weekly') &&
                isShowWithinSameEventWindow(currentShowDateValue, showItem.dateValue)
              ))
              .sort((first, second) => (first.dateValue?.getTime() || 0) - (second.dateValue?.getTime() || 0));

            setSameEventShows(nextSameEventShows);
            setSelectedMoveTargetShowId((prev) =>
              nextSameEventShows.some((showItem) => showItem.id === prev)
                ? prev
                : nextSameEventShows[0]?.id || ''
            );
          } else {
            setSameEventShows([]);
            setSelectedMoveTargetShowId('');
          }

          setProgress(25);
          const partsRef = collection(db, 'shows', showId, 'participations');
          const partsSnap = await getDocs(partsRef);
          const partsData = await Promise.all(
            partsSnap.docs.map(async (partDoc) => {
              const data = partDoc.data();
              const wrestlerRef = doc(db, 'wrestlers', data.wrestlerId);
              const wrestlerSnap = await getDoc(wrestlerRef);

              return {
                id: partDoc.id,
                ...data,
                wrestlerName: wrestlerSnap?.data()?.name || 'Sconosciuto'
              };
            })
          );
          const sortedParticipations = partsData.sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName));
          setParticipations(sortedParticipations);

          setProgress(45);
          const draftRef = collection(db, 'shows', showId, 'draft');
          const draftSnap = await getDocs(draftRef);
          const draftData = await Promise.all(
            draftSnap.docs.map(async (partDoc) => {
              const partData = partDoc.data();
              const wrestlerRef = doc(db, 'wrestlers', partData.wrestlerId);
              const wrestlerSnap = await getDoc(wrestlerRef);

              return {
                id: partDoc.id,
                ...partData,
                wrestlerName: wrestlerSnap?.data()?.name || 'Sconosciuto'
              };
            })
          );
          setDraftParticipations(draftData.sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName)));

          setProgress(65);
          const matchesRef = collection(db, 'shows', showId, 'matches');
          const matchesSnap = await getDocs(matchesRef);
          const matchesData = await Promise.all(
            matchesSnap.docs.map(async (matchDoc) => {
              const data = matchDoc.data();

              const participantIds = Array.isArray(data.participants) ? data.participants : [];
              const participantsDetails = await Promise.all(
                participantIds.map(async (wrestlerId) => {
                  if (typeof wrestlerId === 'string' && wrestlerId.startsWith('guest_')) {
                    const guestEntry = Array.isArray(data.guests) ? data.guests.find(g => g.id === wrestlerId) : null;
                    return {
                      id: wrestlerId,
                      name: guestEntry?.name || 'Ospite',
                      isGuest: true,
                      imageUrl: guestEntry?.imageUrl || null
                    };
                  }
                  const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
                  const wrestlerSnap = await getDoc(wrestlerRef);
                  return {
                    id: wrestlerId,
                    name: wrestlerSnap?.data()?.name || 'Sconosciuto'
                  };
                })
              );

              let teamsDetails = [];
              if (Array.isArray(data.teams)) {
                teamsDetails = await Promise.all(
                  data.teams.map(async (team) => {
                    const teamParticipantIds = Array.isArray(team.participants) ? team.participants : [];
                    const teamParticipantsDetails = await Promise.all(
                      teamParticipantIds.map(async (wrestlerId) => {
                        if (typeof wrestlerId === 'string' && wrestlerId.startsWith('guest_')) {
                          const guestEntry = Array.isArray(data.guests) ? data.guests.find(g => g.id === wrestlerId) : null;
                          return {
                            id: wrestlerId,
                            name: guestEntry?.name || 'Ospite',
                            isGuest: true,
                            imageUrl: guestEntry?.imageUrl || null
                          };
                        }
                        const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
                        const wrestlerSnap = await getDoc(wrestlerRef);
                        return {
                          id: wrestlerId,
                          name: wrestlerSnap?.data()?.name || 'Sconosciuto'
                        };
                      })
                    );
                    return {
                      ...team,
                      participantsDetails: teamParticipantsDetails
                    };
                  })
                );
              }

              return {
                id: matchDoc.id,
                ...data,
                participantsDetails,
                teamsDetails
              };
            })
          );
          setMatches(matchesData);

          setProgress(82);
          const wrestlersSnap = await getDocs(collection(db, 'wrestlers'));
          const wrestlersData = wrestlersSnap.docs
            .map(d => {
              const wrestlerData = d.data();
              return {
                id: d.id,
                name: wrestlerData.name || wrestlerData.nome || 'Sconosciuto',
                score: Number(wrestlerData.score) || 0,
                brand: wrestlerData.brand || '',
                gender: wrestlerData.gender,
                imageUrl: wrestlerData.imageUrl || '',
                isGuest: Boolean(wrestlerData.isGuest),
                championTypes: Array.isArray(wrestlerData.championTypes)
                  ? wrestlerData.championTypes.filter(Boolean)
                  : [],
                currentChampionship: wrestlerData.currentChampionship || '',
                championship: wrestlerData.championship || '',
                active: wrestlerData.active !== false
              };
            })
            .sort((a, b) => a.name.localeCompare(b.name));
          setWrestlers(wrestlersData);

          const teamsSnap = await getDocs(collection(db, 'teams'));
          const wrestlerNamesById = wrestlersData.reduce((acc, wrestler) => {
            acc[wrestler.id] = wrestler.name;
            return acc;
          }, {});
          const savedTeamsData = teamsSnap.docs
            .map((teamDoc) => {
              const teamData = teamDoc.data();
              const members = Array.isArray(teamData.members) ? teamData.members : [];
              return {
                id: teamDoc.id,
                name: teamData.name || 'Team senza nome',
                members,
                memberNames: members.map((memberId) => wrestlerNamesById[memberId]).filter(Boolean)
              };
            })
            .filter((team) => team.members.length > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
          setSavedTeams(savedTeamsData);

          try {
            // 1) Ospiti salvati come wrestler nel db (dati minimi, flag isGuest)
            const guestByName = new Map();
            wrestlersData
              .filter((wrestler) => wrestler.isGuest)
              .sort((a, b) => a.name.localeCompare(b.name))
              .forEach((wrestler) => {
                const name = wrestler.name?.trim();
                if (!name) return;
                guestByName.set(name.toLowerCase(), {
                  name,
                  imageUrl: wrestler.imageUrl || '',
                  source: 'db'
                });
              });

            // 2) Ospiti ad-hoc gia' usati in show precedenti (solo se non gia' salvati nel db)
            const allMatchesSnap = await getDocs(collectionGroup(db, 'matches'));
            allMatchesSnap.docs.forEach((matchDoc) => {
              const matchGuests = matchDoc.data().guests;
              if (!Array.isArray(matchGuests)) return;

              matchGuests.forEach((guest) => {
                const name = guest?.name?.trim();
                if (!name) return;
                const key = name.toLowerCase();
                const existing = guestByName.get(key);
                if (!existing) {
                  guestByName.set(key, { name, imageUrl: guest.imageUrl || '', source: 'history' });
                } else if (existing.source === 'history' && !existing.imageUrl && guest.imageUrl) {
                  existing.imageUrl = guest.imageUrl;
                }
              });
            });

            const dbGuests = [...guestByName.values()].filter((guest) => guest.source === 'db');
            const historyGuests = [...guestByName.values()].filter((guest) => guest.source === 'history');
            setSavedGuests([...dbGuests, ...historyGuests]);
          } catch (guestError) {
            console.error('Errore caricamento ospiti salvati:', guestError);
          }

          setProgress(100);
        } catch (error) {
          console.error('Errore caricamento dati:', error);
          setMessage('Errore nel caricamento dei dati');
        } finally {
          setLoading(false);
        }
      });
    };

    fetchData();
  }, [showId]);

  useEffect(() => {
    const sum = selectedBonuses.reduce(
      (acc, b) => acc + b.points * b.qty,
      0
    );
    setTotalPoints(sum);
  }, [selectedBonuses]);

  useEffect(() => {
    setSelectedWrestlers([]);
    setSelectedBonuses([]);
    setEditingParticipationId(null);
    setBulkEditMode(false);
    setSelectedParticipationsForEdit([]);
    setSelectedPreset(null);
  }, [participationMode]);

  const handleBonusToggle = (bonus) => {
    setSelectedBonuses(prev => {
      const exists = prev.find(b => b.value === bonus.value);
      if (exists) {
        return prev.filter(b => b.value !== bonus.value);
      } else {
        return [...prev, { ...bonus, qty: 1 }];
      }
    });
  };

  const handleQtyChange = (value, newQty) => {
    setSelectedBonuses(prev =>
      prev.map(b =>
        b.value === value
          ? { ...b, qty: Math.max(1, newQty) }
          : b
      )
    );
  };

  const activeParticipations = participationMode === 'draft' ? draftParticipations : participations;
  const showEventDate = useMemo(() => {
    const value = getShowScoringDate(showData);
    return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
  }, [showData]);
  const liveBonusUnlockDate = useMemo(() => {
    if (!showEventDate) return null;
    return new Date(showEventDate.getTime() - LIVE_BONUS_LOCK_MS);
  }, [showEventDate]);

  useEffect(() => {
    let isMounted = true;

    const loadBonusSuggestions = async () => {
      const championWrestlers = wrestlers.filter((wrestler) =>
        Array.isArray(wrestler.championTypes) && wrestler.championTypes.some(Boolean)
      );

      if (!showEventDate || championWrestlers.length === 0) {
        if (isMounted) {
          setBonusSuggestionsByWrestler({});
          setBonusSuggestionsLoading(false);
        }
        return;
      }

      setBonusSuggestionsLoading(true);

      const titleValuesByWrestler = championWrestlers.reduce((acc, wrestler) => {
        acc[wrestler.id] = wrestler.championTypes.filter(Boolean);
        return acc;
      }, {});

      try {
        const {
          titleStatsByWrestler,
          bonusHistoryByWrestler,
          shows
        } = await loadTitleBonusSuggestionBundle({
          db,
          wrestlerIds: championWrestlers.map((wrestler) => wrestler.id),
          titleValuesByWrestler,
          referenceDate: showEventDate
        });

        if (!isMounted) return;

        const nextSuggestions = championWrestlers.reduce((acc, wrestler) => {
          acc[wrestler.id] = buildTitleBonusSuggestionsForWrestler({
            wrestler,
            titleStatsByTitle: titleStatsByWrestler[wrestler.id] || {},
            bonusHistoryByTitle: bonusHistoryByWrestler[wrestler.id] || {},
            shows,
            currentShowId: showId
          });
          return acc;
        }, {});

        setBonusSuggestionsByWrestler(nextSuggestions);
      } catch (error) {
        console.error('Errore nel caricamento dei suggerimenti bonus titolati:', error);
        if (isMounted) {
          setBonusSuggestionsByWrestler({});
        }
      } finally {
        if (isMounted) {
          setBonusSuggestionsLoading(false);
        }
      }
    };

    loadBonusSuggestions();

    return () => {
      isMounted = false;
    };
  }, [showEventDate, showId, wrestlers]);

  const isLiveBonusLocked = Boolean(
    liveBonusUnlockDate && editorNow < liveBonusUnlockDate.getTime()
  );
  const liveBonusUnlockLabel = useMemo(
    () => getReadableDateTimeLabel(liveBonusUnlockDate),
    [liveBonusUnlockDate]
  );
  const wrestlerScoresById = useMemo(
    () =>
      wrestlers.reduce((acc, wrestler) => {
        acc[wrestler.id] = Number(wrestler.score) || 0;
        return acc;
      }, {}),
    [wrestlers]
  );
  const assignedMatchParticipantIds = useMemo(
    () => getMatchAssignedParticipantIds(matches),
    [matches]
  );

  const handleDeleteParticipation = async (participationId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa partecipazione?')) return;

    await runWithBusy('Eliminazione partecipazione...', async () => {
      try {
        const targetCollection = participationMode === 'draft' ? 'draft' : 'participations';
        const participation = activeParticipations.find(p => p.id === participationId);

        await deleteDoc(doc(db, 'shows', showId, targetCollection, participationId));

        if (participationMode === 'live' && participation) {
          const wrestlerRef = doc(db, 'wrestlers', participation.wrestlerId);
          const wrestlerSnap = await getDoc(wrestlerRef);
          const newScore = (wrestlerSnap.data()?.score || 0) - participation.totalPoints;
          await updateDoc(wrestlerRef, { score: newScore });
          updateLocalWrestlerScore(participation.wrestlerId, -participation.totalPoints);
          await applyParticipationDeltaToFactionScores({
            db,
            wrestlerId: participation.wrestlerId,
            rawPointsDelta: -participation.totalPoints,
            showDate: getShowScoringDate(showData)
          });
          setParticipations(prev => prev.filter(p => p.id !== participationId));
          setMessage('Partecipazione eliminata con successo');
        } else {
          setDraftParticipations(prev => prev.filter(p => p.id !== participationId));
          setMessage('Bozza partecipazione eliminata con successo');
        }
        await recordAdminUpdate({
          type: 'Partecipazioni',
          action: 'Eliminazione',
          title: participation?.wrestlerName || showName || showId,
          description: `${participationMode === 'draft' ? 'Bozza partecipazione eliminata' : 'Partecipazione eliminata'} per ${showName || 'show'}`,
          entityType: 'shows',
          entityId: showId
        });
      } catch (error) {
        console.error('Errore eliminazione:', error);
        setMessage('Errore durante l\'eliminazione');
      }
    });
  };

  const handleEditParticipation = (participation) => {
    setSelectedWrestlers([participation.wrestlerId]);
    const bonusMap = new Map();
    const bonuses = Array.isArray(participation.bonuses) ? participation.bonuses : [];
    const storedBonusPoints = getBonusPointsForRecord(bonuses, participation.bonusPoints);

    bonuses.forEach((value, index) => {
      const displayBonus = getStoredBonusDisplay(
        value,
        (baseValue) => BONUS_LIST.find((item) => item.value === baseValue),
        showData,
        storedBonusPoints[index]
      );
      const bonus = displayBonus.bonus;
      const bonusPoints = displayBonus.points;
      if (!bonus || bonusPoints === null) return;

      const key = `${displayBonus.baseValue}__${bonusPoints}`;
      const existing = bonusMap.get(key);
      bonusMap.set(key, {
        value: bonus.value,
        label: bonus.label,
        points: bonusPoints,
        qty: (existing?.qty || 0) + 1
      });
    });

    const newSelectedBonuses = Array.from(bonusMap.values());

    setSelectedBonuses(newSelectedBonuses);
    setTotalPoints(participation.totalPoints);
    setEditingParticipationId(participation.id);
    setBulkEditMode(false);
    setSelectedParticipationsForEdit([]);
  };

  const handleCancelEdit = () => {
    setSelectedWrestlers([]);
    setSelectedBonuses([]);
    setEditingParticipationId(null);
    setBulkEditMode(false);
    setSelectedParticipationsForEdit([]);
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }

    if (bulkEditMode && selectedParticipationsForEdit.length === 0) {
      setMessage('Seleziona almeno una partecipazione da modificare');
      return;
    } else if (!bulkEditMode && selectedWrestlers.length === 0) {
      setMessage('Seleziona almeno un wrestler');
      return;
    }

    const editedParticipation = editingParticipationId
      ? activeParticipations.find((participation) => participation.id === editingParticipationId)
      : null;
    const shouldLockLiveBonuses = participationMode === 'live' && isLiveBonusLocked;

    if (shouldLockLiveBonuses && bulkEditMode) {
      setMessage('I bonus live si possono aggiungere solo nelle 48 ore prima dello show. Usa il draft per prepararli.');
      return;
    }

    const preservedBonuses = Array.isArray(editedParticipation?.bonuses) ? editedParticipation.bonuses : [];
    const preservedBonusPoints = getBonusPointsForRecord(preservedBonuses, editedParticipation?.bonusPoints);
    const nextBonusesForRecord = shouldLockLiveBonuses
      ? (editingParticipationId ? preservedBonuses : [])
      : selectedBonuses.flatMap(b =>
          Array.from({ length: b.qty }, () => b.value)
        );
    const nextBonusPointsForRecord = shouldLockLiveBonuses
      ? (editingParticipationId ? preservedBonusPoints : [])
      : buildBonusPointsForRecord(selectedBonuses);
    const nextBonusPoints = shouldLockLiveBonuses
      ? (editingParticipationId ? Number(editedParticipation?.totalPoints) || 0 : 0)
      : selectedBonuses.reduce(
          (acc, b) => acc + b.points * b.qty,
          0
        );

    const liveAnnouncementMode = participationMode === 'live' && shouldLockLiveBonuses;
    const newBonusLabelsForRecord = selectedBonuses.flatMap(b =>
      Array.from({ length: b.qty }, () => b.value)
    );

    await runWithBusy(participationMode === 'draft' ? 'Salvataggio bozze...' : 'Salvataggio partecipazioni...', async () => {
      try {
        if (bulkEditMode) {
          const updatedParticipations = [];

          for (const participationId of selectedParticipationsForEdit) {
            const originalParticipation = activeParticipations.find(p => p.id === participationId);
            const originalBonuses = Array.isArray(originalParticipation.bonuses) ? originalParticipation.bonuses : [];

            const combinedBonuses = [
              ...originalBonuses,
              ...nextBonusesForRecord
            ];
            const combinedBonusPoints = [
              ...getBonusPointsForRecord(originalBonuses, originalParticipation.bonusPoints),
              ...nextBonusPointsForRecord
            ];

            const newTotalPoints = originalParticipation.totalPoints + nextBonusPoints;

            await updateDoc(doc(db, 'shows', showId, participationMode === 'draft' ? 'draft' : 'participations', participationId), {
              bonuses: combinedBonuses,
              bonusPoints: combinedBonusPoints,
              totalPoints: newTotalPoints
            });

            if (participationMode === 'live') {
              const wrestlerRef = doc(db, 'wrestlers', originalParticipation.wrestlerId);
              const wrestlerSnap = await getDoc(wrestlerRef);
              const currentScore = wrestlerSnap.data()?.score || 0;
              const newScore = currentScore + nextBonusPoints;
              await updateDoc(wrestlerRef, { score: newScore });
              updateLocalWrestlerScore(originalParticipation.wrestlerId, nextBonusPoints);
              await applyParticipationDeltaToFactionScores({
                db,
                wrestlerId: originalParticipation.wrestlerId,
                rawPointsDelta: nextBonusPoints,
                showDate: getShowScoringDate(showData)
              });
            }

            updatedParticipations.push({
              id: participationId,
              wrestlerId: originalParticipation.wrestlerId,
              wrestlerName: originalParticipation.wrestlerName,
              bonuses: combinedBonuses,
              bonusPoints: combinedBonusPoints,
              totalPoints: newTotalPoints
            });
          }

          const mergeUpdatedParticipations = (prev) =>
            prev.map(p =>
              selectedParticipationsForEdit.includes(p.id)
                ? updatedParticipations.find(up => up.id === p.id) || p
                : p
            ).sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName));

          if (participationMode === 'draft') {
            setDraftParticipations(mergeUpdatedParticipations);
            setMessage(`${updatedParticipations.length} bozze aggiornate: bonus aggiunti con successo`);
          } else {
            setParticipations(mergeUpdatedParticipations);
            setMessage(`${updatedParticipations.length} partecipazioni aggiornate: bonus aggiunti con successo`);
          }
          await recordAdminUpdate({
            type: 'Partecipazioni',
            action: 'Modifica',
            title: showName || showId,
            description: `${updatedParticipations.length} ${participationMode === 'draft' ? 'bozze' : 'partecipazioni'} show aggiornate: bonus aggiunti ${newBonusLabelsForRecord.join(', ') || 'nessuno'}, punti +${nextBonusPoints}`,
            entityType: 'shows',
            entityId: showId
          });
          if (participationMode === 'draft') {
            await resetAutoPublishDraftHandling();
          }
        } else if (editingParticipationId) {
          const participationRef = doc(db, 'shows', showId, participationMode === 'draft' ? 'draft' : 'participations', editingParticipationId);
          const originalParticipation = activeParticipations.find(p => p.id === editingParticipationId);
          const pointsDifference = nextBonusPoints - originalParticipation.totalPoints;

          await updateDoc(participationRef, {
            bonuses: nextBonusesForRecord,
            bonusPoints: nextBonusPointsForRecord,
            totalPoints: nextBonusPoints
          });

          if (participationMode === 'live') {
            const wrestlerRef = doc(db, 'wrestlers', originalParticipation.wrestlerId);
            const wrestlerSnap = await getDoc(wrestlerRef);
            const currentScore = wrestlerSnap.data()?.score || 0;
            const newScore = currentScore + pointsDifference;
            await updateDoc(wrestlerRef, { score: newScore });
            updateLocalWrestlerScore(originalParticipation.wrestlerId, pointsDifference);
            await applyParticipationDeltaToFactionScores({
              db,
              wrestlerId: originalParticipation.wrestlerId,
              rawPointsDelta: pointsDifference,
              showDate: getShowScoringDate(showData)
            });
          }

          const mergeEditedParticipation = (prev) =>
            prev.map(p =>
              p.id === editingParticipationId
                ? { ...p, bonuses: nextBonusesForRecord, bonusPoints: nextBonusPointsForRecord, totalPoints: nextBonusPoints }
                : p
            ).sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName));

          if (participationMode === 'draft') {
            setDraftParticipations(mergeEditedParticipation);
            setMessage('Bozza partecipazione aggiornata con successo');
          } else {
            setParticipations(mergeEditedParticipation);
            setMessage('Partecipazione aggiornata con successo');
          }
          await recordAdminUpdate({
            type: 'Partecipazioni',
            action: 'Modifica',
            title: originalParticipation?.wrestlerName || showName || showId,
            description: buildChangeDescription(
              `${participationMode === 'draft' ? 'Bozza partecipazione' : 'Partecipazione'} aggiornata`,
              originalParticipation,
              {
                bonuses: nextBonusesForRecord,
                bonusPoints: nextBonusPointsForRecord,
                totalPoints: nextBonusPoints
              },
              [
                {
                  key: 'bonuses',
                  label: 'bonus',
                  format: value => Array.isArray(value) && value.length ? value.join(', ') : 'nessuno'
                },
                {
                  key: 'bonusPoints',
                  label: 'punti bonus',
                  format: value => Array.isArray(value) && value.length ? value.join(', ') : 'nessuno'
                },
                { key: 'totalPoints', label: 'punti totali' }
              ]
            ),
            entityType: 'shows',
            entityId: showId
          });
          if (participationMode === 'draft') {
            await resetAutoPublishDraftHandling();
          }
        } else {
          const newParticipations = [];
          const targetCollection = participationMode === 'draft' ? 'draft' : 'participations';

          for (const wrestlerId of selectedWrestlers) {
            const participationDoc = await addDoc(collection(db, 'shows', showId, targetCollection), {
              wrestlerId: wrestlerId,
              bonuses: nextBonusesForRecord,
              bonusPoints: nextBonusPointsForRecord,
              totalPoints: nextBonusPoints,
              timestamp: new Date()
            });

            if (participationMode === 'live') {
              const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
              const wrestlerSnap = await getDoc(wrestlerRef);
              const currentScore = wrestlerSnap.data()?.score || 0;
              const newScore = currentScore + nextBonusPoints;
              await updateDoc(wrestlerRef, { score: newScore });
              updateLocalWrestlerScore(wrestlerId, nextBonusPoints);
              await applyParticipationDeltaToFactionScores({
                db,
                wrestlerId,
                rawPointsDelta: nextBonusPoints,
                showDate: getShowScoringDate(showData)
              });
            }

            const wrestler = wrestlers.find(w => w.id === wrestlerId);
            newParticipations.push({
              id: participationDoc.id,
              wrestlerId: wrestlerId,
              wrestlerName: wrestler?.name || 'Sconosciuto',
              bonuses: nextBonusesForRecord,
              bonusPoints: nextBonusPointsForRecord,
              totalPoints: nextBonusPoints
            });
          }

          if (participationMode === 'draft') {
            setDraftParticipations(prev => [...prev, ...newParticipations].sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName)));
            setMessage(`Bozze create con successo per ${newParticipations.length} wrestler`);
          } else {
            setParticipations(prev => [...prev, ...newParticipations].sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName)));
            setMessage(
              liveAnnouncementMode
                ? `Wrestler annunciati in live per ${newParticipations.length} partecipazioni`
                : `Partecipazioni create con successo per ${newParticipations.length} wrestler`
            );
          }
          await recordAdminUpdate({
            type: 'Partecipazioni',
            action: participationMode === 'draft' ? 'Creazione bozza' : 'Creazione',
            title: showName || showId,
            description: `${newParticipations.length} ${participationMode === 'draft' ? 'bozze create' : liveAnnouncementMode ? 'annunci live creati' : 'partecipazioni create'}: ${newParticipations.map(item => item.wrestlerName).join(', ')}; bonus: ${nextBonusesForRecord.join(', ') || 'nessuno'}; punti: ${nextBonusPoints}`,
            entityType: 'shows',
            entityId: showId
          });
          if (participationMode === 'draft') {
            await resetAutoPublishDraftHandling();
          }
        }

        setSelectedWrestlers([]);
        setSelectedBonuses([]);
        setEditingParticipationId(null);
        setBulkEditMode(false);

      } catch (error) {
        console.error('Errore salvataggio:', error);
        setMessage('Errore durante il salvataggio');
      }
    });
  };


  const applyPreset = (preset) => {
    setSelectedPreset(preset.label);
    setSelectedBonuses(
      preset.values.map(value => {
        const bonus = BONUS_LIST.find(b => b.value === value);
        const bonusPoints = getBonusPointsForShow(bonus, showData);
        if (!bonus || !isBonusAvailableForShow(bonus, showData) || bonusPoints === null) {
          return null;
        }

        return {
          value: bonus.value,
          label: bonus.label,
          points: bonusPoints,
          qty: 1
        };
      }).filter(Boolean)
    );
  };

  const handleWrestlerSelection = (wrestlerId) => {
    setSelectedWrestlers(prev => {
      if (prev.includes(wrestlerId)) {
        return prev.filter(id => id !== wrestlerId);
      }
      return [...prev, wrestlerId];
    });
  };

  const handleWrestlerRemove = (wrestlerId) => {
    setSelectedWrestlers(prev => prev.filter(id => id !== wrestlerId));
  };

  const handleParticipationSelection = (participationId) => {
    if (editingParticipationId) return;

    setSelectedParticipationsForEdit(prev => {
      if (prev.includes(participationId)) {
        return prev.filter(id => id !== participationId);
      }
      return [...prev, participationId];
    });
  };

  const enableBulkEdit = () => {
    if (selectedParticipationsForEdit.length === 0) {
      setMessage('Seleziona almeno una partecipazione a cui aggiungere bonus');
      return;
    }

    setSelectedWrestlers([]);
    setSelectedBonuses([]);
    setEditingParticipationId(null);
    setBulkEditMode(true);
    setMessage(`Modalità aggiunta bonus attiva: seleziona i bonus da aggiungere a ${selectedParticipationsForEdit.length} partecipazioni`);
  };

  const handleMoveParticipationsToSiblingShow = async () => {
    if (participationMode !== 'live') return;

    if (bulkEditMode) {
      setMessage('Esci dalla modifica multipla prima di spostare le partecipazioni.');
      return;
    }

    if (!selectedMoveTargetShowId) {
      setMessage('Seleziona una data di destinazione.');
      return;
    }

    if (selectedParticipationsForEdit.length === 0) {
      setMessage('Seleziona almeno una partecipazione live da spostare.');
      return;
    }

    const targetShow = sameEventShows.find((showItem) => showItem.id === selectedMoveTargetShowId);
    if (!targetShow) {
      setMessage('La data di destinazione non e disponibile.');
      return;
    }

    const selectedLiveParticipations = participations.filter((participation) =>
      selectedParticipationsForEdit.includes(participation.id)
    );

    if (selectedLiveParticipations.length === 0) {
      setMessage('Le partecipazioni selezionate non sono piu disponibili.');
      return;
    }

    const lockedByMatches = selectedLiveParticipations.filter((participation) =>
      assignedMatchParticipantIds.has(participation.wrestlerId)
    );
    const movableParticipations = selectedLiveParticipations.filter((participation) =>
      !assignedMatchParticipantIds.has(participation.wrestlerId)
    );

    if (movableParticipations.length === 0) {
      setMessage(
        lockedByMatches.length === 1
          ? `${lockedByMatches[0].wrestlerName} e gia inserito in un match di questa data.`
          : 'Le partecipazioni selezionate sono gia usate nei match di questa data e non possono essere spostate.'
      );
      return;
    }

    const destinationLabel = targetShow.label || formatEventDateOptionLabel(targetShow.data);
    const shouldMove = window.confirm(
      `Sposti ${movableParticipations.length} partecipazioni live su ${destinationLabel}?` +
        (lockedByMatches.length
          ? ` ${lockedByMatches.length} resteranno qui perche gia presenti nei match di questa data.`
          : '')
    );

    if (!shouldMove) return;

    setMovingParticipations(true);

    await runWithBusy('Spostamento partecipazioni live...', async () => {
      try {
        const targetParticipationsSnap = await getDocs(
          collection(db, 'shows', targetShow.id, 'participations')
        );
        const targetWrestlerIds = new Set(
          targetParticipationsSnap.docs
            .map((partDoc) => partDoc.data()?.wrestlerId)
            .filter(Boolean)
        );
        const sourceShowDate = getShowScoringDate(showData);
        const targetShowDate = targetShow.dateValue || getShowScoringDate({ data: targetShow.data });
        const movedIds = [];
        const movedNames = [];
        const duplicateNames = [];

        for (const participation of movableParticipations) {
          if (targetWrestlerIds.has(participation.wrestlerId)) {
            duplicateNames.push(participation.wrestlerName);
            continue;
          }

          const pointsDelta = Number(participation.totalPoints) || 0;
          await setDoc(
            doc(db, 'shows', targetShow.id, 'participations', participation.id),
            buildStoredParticipationPayload(participation)
          );
          await deleteDoc(doc(db, 'shows', showId, 'participations', participation.id));
          await applyParticipationDeltaToFactionScores({
            db,
            wrestlerId: participation.wrestlerId,
            rawPointsDelta: -pointsDelta,
            showDate: sourceShowDate
          });
          await applyParticipationDeltaToFactionScores({
            db,
            wrestlerId: participation.wrestlerId,
            rawPointsDelta: pointsDelta,
            showDate: targetShowDate
          });

          targetWrestlerIds.add(participation.wrestlerId);
          movedIds.push(participation.id);
          movedNames.push(participation.wrestlerName);
        }

        if (movedIds.length > 0) {
          setParticipations((prev) =>
            prev
              .filter((participation) => !movedIds.includes(participation.id))
              .sort((first, second) => first.wrestlerName.localeCompare(second.wrestlerName))
          );
          setSelectedParticipationsForEdit((prev) =>
            prev.filter((participationId) => !movedIds.includes(participationId))
          );
          setBulkEditMode(false);
        }

        const feedbackParts = [];
        if (movedNames.length > 0) {
          feedbackParts.push(`${movedNames.length} partecipazioni spostate su ${destinationLabel}`);
        }
        if (duplicateNames.length > 0) {
          feedbackParts.push(`${duplicateNames.length} gia presenti nella data di destinazione`);
        }
        if (lockedByMatches.length > 0) {
          feedbackParts.push(`${lockedByMatches.length} bloccate perche gia usate nei match`);
        }

        setMessage(
          feedbackParts.length > 0
            ? `${feedbackParts.join('. ')}.`
            : 'Nessuna partecipazione e stata spostata.'
        );

        if (movedNames.length > 0) {
          await recordAdminUpdate({
            type: 'Partecipazioni',
            action: 'Spostamento data',
            title: showName || showId,
            description: `${movedNames.length} partecipazioni live spostate da ${formatEventDateOptionLabel(showData?.data)} a ${destinationLabel}: ${movedNames.join(', ')}`,
            entityType: 'shows',
            entityId: showId
          });
        }
      } catch (error) {
        console.error('Errore durante lo spostamento delle partecipazioni:', error);
        setMessage('Errore durante lo spostamento delle partecipazioni.');
      } finally {
        setMovingParticipations(false);
      }
    });
  };

  const handleToggleAutoPublishDrafts = async (nextValue) => {
    const previousValue = autoPublishDraftsAtNoon;
    const previousShowData = showData;
    const nextTargetDate = nextValue
      ? resolveAutoPublishTargetDate(
          showData?.data,
          showData?.autoPublishDraftsTargetAt,
          new Date(),
          { ensureFuture: true }
        )
      : null;
    const payload = {
      autoPublishDraftsAtNoon: nextValue,
      autoPublishDraftsTargetAt: nextTargetDate || null
    };

    if (nextValue) {
      payload.autoPublishDraftsHandledForDate = null;
      payload.autoPublishDraftsHandledAt = null;
    }

    setAutoPublishDraftsAtNoon(nextValue);
    setShowData((prev) => (prev ? { ...prev, ...payload } : prev));

    await runWithBusy(
      nextValue ? 'Attivazione pubblicazione automatica bozze...' : 'Disattivazione pubblicazione automatica bozze...',
      async () => {
        try {
          await updateDoc(doc(db, 'shows', showId), payload);
          await recordAdminUpdate({
            type: 'Show',
            action: 'Modifica',
            title: showName || showId,
            description: nextValue
              ? `Pubblicazione automatica bozze attivata per ${formatAutoPublishTargetLabel(nextTargetDate) || 'la prossima finestra utile'} in ${showName || showId}`
              : `Pubblicazione automatica bozze disattivata per lo show ${showName || showId}`,
            entityType: 'shows',
            entityId: showId
          });
          setMessage(
            nextValue
              ? `Pubblicazione automatica bozze attivata per ${formatAutoPublishTargetLabel(nextTargetDate) || 'la prossima finestra utile'}`
              : 'Pubblicazione automatica bozze disattivata'
          );
        } catch (error) {
          console.error('Errore aggiornamento pubblicazione automatica bozze:', error);
          setAutoPublishDraftsAtNoon(previousValue);
          setShowData(previousShowData);
          setMessage('Errore durante l\'aggiornamento della pubblicazione automatica bozze');
        }
      }
    );
  };

  const resetAutoPublishDraftHandling = async () => {
    if (!autoPublishDraftsAtNoon) return;

    const nextTargetDate = resolveAutoPublishTargetDate(
      showData?.data,
      showData?.autoPublishDraftsTargetAt,
      new Date(),
      { ensureFuture: true }
    );
    const payload = {
      autoPublishDraftsHandledForDate: null,
      autoPublishDraftsHandledAt: null,
      autoPublishDraftsTargetAt: nextTargetDate || null
    };

    setShowData((prev) => (prev ? { ...prev, ...payload } : prev));

    try {
      await updateDoc(doc(db, 'shows', showId), payload);
    } catch (error) {
      console.warn('Impossibile resettare lo stato della pubblicazione automatica draft:', error);
    }
  };

  const handlePublishDrafts = async (options = {}) => {
    const { skipConfirm = false } = options || {};
    if (publishingDraftsRef.current) return;

    if (draftParticipations.length === 0) return;

    const draftLabel = draftParticipations.length === 1 ? 'bozza' : 'bozze';
    const shouldPublish = skipConfirm || window.confirm(
      `Confermi la pubblicazione di ${draftParticipations.length} ${draftLabel}? Le bozze verranno spostate nelle partecipazioni definitive e aggiorneranno i punteggi.`
    );

    if (!shouldPublish) return;

    publishingDraftsRef.current = true;
    setPublishingDrafts(true);

    try {
      await runWithBusy('Pubblicazione bozze...', async (setProgress) => {
        try {
          const updatedLiveParticipations = [...participations];
          const handledDraftIds = [];
          const totalDrafts = draftParticipations.length;
          let publishedCount = 0;
          const updatePublishProgress = () => {
            setProgress(12 + (handledDraftIds.length / totalDrafts) * 82);
          };

          setProgress(12);

          for (const draft of draftParticipations) {
            const existingIndex = updatedLiveParticipations.findIndex(p => p.wrestlerId === draft.wrestlerId);
            const existingParticipation = existingIndex !== -1 ? updatedLiveParticipations[existingIndex] : null;

            const result = await runTransaction(db, async (transaction) => {
              const draftRef = doc(db, 'shows', showId, 'draft', draft.id);
              const draftSnap = await transaction.get(draftRef);

              if (!draftSnap.exists()) {
                return { published: false, draftId: draft.id };
              }

              const latestDraft = draftSnap.data();
              const wrestlerId = latestDraft.wrestlerId || draft.wrestlerId;
              const draftBonuses = Array.isArray(latestDraft.bonuses) ? latestDraft.bonuses : [];
              const draftBonusPoints = getBonusPointsForRecord(draftBonuses, latestDraft.bonusPoints);
              const draftTotalPoints = Number(latestDraft.totalPoints) || 0;
              const participationRef = existingParticipation
                ? doc(db, 'shows', showId, 'participations', existingParticipation.id)
                : doc(db, 'shows', showId, 'participations', wrestlerId);
              const participationSnap = await transaction.get(participationRef);

              let nextParticipation;

              if (participationSnap.exists()) {
                const participationData = participationSnap.data();
                const currentBonuses = Array.isArray(participationData.bonuses) ? participationData.bonuses : [];
                const currentBonusPoints = getBonusPointsForRecord(currentBonuses, participationData.bonusPoints);
                const mergedBonuses = [...currentBonuses, ...draftBonuses];
                const mergedBonusPoints = [...currentBonusPoints, ...draftBonusPoints];
                const newTotalPoints = (Number(participationData.totalPoints) || 0) + draftTotalPoints;

                transaction.update(participationRef, {
                  bonuses: mergedBonuses,
                  bonusPoints: mergedBonusPoints,
                  totalPoints: newTotalPoints
                });

                nextParticipation = {
                  id: participationRef.id,
                  wrestlerId,
                  wrestlerName: draft.wrestlerName,
                  bonuses: mergedBonuses,
                  bonusPoints: mergedBonusPoints,
                  totalPoints: newTotalPoints
                };
              } else {
                transaction.set(participationRef, {
                  wrestlerId,
                  bonuses: draftBonuses,
                  bonusPoints: draftBonusPoints,
                  totalPoints: draftTotalPoints,
                  timestamp: new Date()
                });

                nextParticipation = {
                  id: participationRef.id,
                  wrestlerId,
                  wrestlerName: draft.wrestlerName,
                  bonuses: draftBonuses,
                  bonusPoints: draftBonusPoints,
                  totalPoints: draftTotalPoints
                };
              }

              if (draftTotalPoints !== 0) {
                transaction.update(doc(db, 'wrestlers', wrestlerId), {
                  score: increment(draftTotalPoints)
                });
              }

              transaction.delete(draftRef);

              return {
                published: true,
                draftId: draft.id,
                wrestlerId,
                rawPointsDelta: draftTotalPoints,
                participation: nextParticipation
              };
            });

            if (!result.published) {
              handledDraftIds.push(result.draftId || draft.id);
              updatePublishProgress();
              continue;
            }

            await applyParticipationDeltaToFactionScores({
              db,
              wrestlerId: result.wrestlerId,
              rawPointsDelta: result.rawPointsDelta,
              showDate: getShowScoringDate(showData)
            });
            updateLocalWrestlerScore(result.wrestlerId, result.rawPointsDelta);

            const nextIndex = updatedLiveParticipations.findIndex(p => p.wrestlerId === result.participation.wrestlerId);
            if (nextIndex !== -1) {
              updatedLiveParticipations[nextIndex] = {
                ...updatedLiveParticipations[nextIndex],
                ...result.participation
              };
            } else {
              updatedLiveParticipations.push(result.participation);
            }
            handledDraftIds.push(result.draftId);
            publishedCount += 1;
            updatePublishProgress();
          }

          setProgress(96);
          setParticipations(updatedLiveParticipations.sort((a, b) => a.wrestlerName.localeCompare(b.wrestlerName)));
          setDraftParticipations(prev => prev.filter(draft => !handledDraftIds.includes(draft.id)));
          setMessage(
            publishedCount > 0
              ? 'Tutte le bozze sono state pubblicate con successo'
              : 'Le bozze risultano gia pubblicate'
          );
          if (publishedCount > 0) {
            await recordAdminUpdate({
              type: 'Partecipazioni',
              action: 'Pubblicazione',
              title: showName || showId,
              description: `${publishedCount} bozze pubblicate come partecipazioni definitive`,
              entityType: 'shows',
              entityId: showId
            });
          }
        } catch (error) {
          console.error('Errore durante la pubblicazione delle bozze:', error);
          setMessage('Errore durante la pubblicazione delle bozze');
        }
      });
    } finally {
      publishingDraftsRef.current = false;
      setPublishingDrafts(false);
    }
  };

  useEffect(() => {
    const targetDate = resolveAutoPublishTargetDate(
      showData?.data,
      showData?.autoPublishDraftsTargetAt
    );
    const isHandled = Boolean(showData?.autoPublishDraftsHandledForDate);

    if (!autoPublishDraftsAtNoon || isHandled || !targetDate) {
      setAutoPublishCountdown(getCountdownParts(targetDate));
      return undefined;
    }

    const updateCountdown = () => {
      setAutoPublishCountdown(getCountdownParts(targetDate));
    };

    updateCountdown();
    const intervalId = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(intervalId);
  }, [
    autoPublishDraftsAtNoon,
    showData?.data,
    showData?.autoPublishDraftsTargetAt,
    showData?.autoPublishDraftsHandledForDate
  ]);

  const availableWrestlersForMatches = useMemo(() => {
    const liveIds = new Set(participations.map(p => p.wrestlerId));
    const draftIds = new Set(draftParticipations.map(p => p.wrestlerId));
    const participatingIds = new Set([...liveIds, ...draftIds]);
    const assignedMatchIds = new Set(
      matches
        .filter(match => match.id !== editingMatchId)
        .flatMap(match => {
          if (Array.isArray(match.participants)) return match.participants;
          if (Array.isArray(match.teams)) {
            return match.teams.flatMap(team => Array.isArray(team.participants) ? team.participants : []);
          }
          return [];
        })
        .filter(Boolean)
    );
    const liveParticipationsByWrestlerId = new Map();
    const draftParticipationsByWrestlerId = new Map();

    participations.forEach((participation) => {
      liveParticipationsByWrestlerId.set(participation.wrestlerId, participation);
    });
    draftParticipations.forEach((participation) => {
      draftParticipationsByWrestlerId.set(participation.wrestlerId, participation);
    });

    const getMatchBonusCount = (participation) => {
      const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : [];
      return bonuses.filter((bonusValue) =>
        MATCH_PRESENCE_BONUS_VALUES.has(normalizeStoredBonusValue(bonusValue))
      ).length;
    };
    const getWinBonusCount = (participation) => {
      const bonuses = Array.isArray(participation?.bonuses) ? participation.bonuses : [];
      return bonuses.filter((bonusValue) =>
        MATCH_WIN_BONUS_VALUES.has(normalizeStoredBonusValue(bonusValue))
      ).length;
    };

    return wrestlers
      .filter(w => participatingIds.has(w.id))
      .map((wrestler) => {
        const liveParticipation = liveParticipationsByWrestlerId.get(wrestler.id);
        const draftParticipation = draftParticipationsByWrestlerId.get(wrestler.id);
        const liveBonuses = Array.isArray(liveParticipation?.bonuses) ? liveParticipation.bonuses : [];
        const draftBonuses = Array.isArray(draftParticipation?.bonuses) ? draftParticipation.bonuses : [];
        const liveMatchBonusCount = getMatchBonusCount(liveParticipation);
        const draftMatchBonusCount = getMatchBonusCount(draftParticipation);
        const matchBonusCount = liveMatchBonusCount + draftMatchBonusCount;
        const liveWinBonusCount = getWinBonusCount(liveParticipation);
        const draftWinBonusCount = getWinBonusCount(draftParticipation);
        const winBonusCount = liveWinBonusCount + draftWinBonusCount;

        return {
          ...wrestler,
          participationStatus: liveIds.has(wrestler.id) ? 'live' : 'draft',
          hasLiveParticipation: liveIds.has(wrestler.id),
          hasDraftParticipation: draftIds.has(wrestler.id),
          participationBonuses: [...liveBonuses, ...draftBonuses],
          hasLiveMatchBonus: liveMatchBonusCount > 0,
          hasDraftMatchBonus: draftMatchBonusCount > 0,
          hasMatchBonus: matchBonusCount > 0,
          matchBonusCount,
          hasLiveWinBonus: liveWinBonusCount > 0,
          hasDraftWinBonus: draftWinBonusCount > 0,
          hasWinBonus: winBonusCount > 0,
          winBonusCount,
          hasAssignedMatch: assignedMatchIds.has(wrestler.id)
        };
      });
  }, [wrestlers, participations, draftParticipations, matches, editingMatchId]);

  const getDefaultTeamName = (teamId) => `Squadra ${String.fromCharCode(64 + Number(teamId || 1))}`;

  const getMatchParticipantName = (participantId) => {
    const guest = guestMap[participantId];
    if (guest?.name) return guest.name;

    const wrestler =
      availableWrestlersForMatches.find(w => w.id === participantId) ||
      wrestlers.find(w => w.id === participantId);

    return wrestler?.name || 'Sconosciuto';
  };

  const getTeamNameFromParticipants = (participants = [], fallbackName = 'Squadra') => {
    if (!participants.length) return fallbackName;
    return participants.map(getMatchParticipantName).join(' & ');
  };

  const withAutoTeamName = (team, fallbackName = null) => ({
    ...team,
    name: getTeamNameFromParticipants(
      team.participants || [],
      fallbackName || getDefaultTeamName(team.id)
    )
  });

  const handleMultiTeamMatchChange = (nextValue) => {
    setIsMultiTeamMatch(nextValue);

    if (nextValue) {
      setTeams(prev => {
        const normalizedTeams = prev.length > 0
          ? prev.map(team => withAutoTeamName(team))
          : [{ id: 1, name: getDefaultTeamName(1), participants: [], color: 'danger' }];

        if (normalizedTeams.length >= 2) return normalizedTeams;

        return [
          normalizedTeams[0],
          {
            id: 2,
            name: getDefaultTeamName(2),
            participants: [],
            color: TEAM_COLORS[1 % TEAM_COLORS.length]
          }
        ];
      });
      return;
    }

    const teamParticipantIds = teams.flatMap(team => team.participants || []);
    if (teamParticipantIds.length > 0) {
      setMatchParticipants(prev => [...new Set([...prev, ...teamParticipantIds])]);
    }
    setTeams([{ id: 1, name: getDefaultTeamName(1), participants: [], color: 'danger' }]);
    if (winner?.startsWith('team')) {
      setWinner('');
    }
  };

  const handleCreateMatch = async (e) => {
    e.preventDefault();

    const totalParticipants = isMultiTeamMatch
      ? teams.reduce((sum, team) => sum + team.participants.length, 0)
      : matchParticipants.length;

    if (totalParticipants < 2) {
      setMessage('Seleziona almeno 2 partecipanti per il match');
      return;
    }

    if (isMultiTeamMatch) {
      const emptyTeams = teams.filter(team => team.participants.length === 0);
      if (emptyTeams.length > 0) {
        setMessage(`Le seguenti squadre non hanno partecipanti: ${emptyTeams.map(t => t.name).join(', ')}`);
        return;
      }
    }

    if (
      isTitleMatch &&
      normalizeMatchTitleValues(matchTitle, secondaryMatchTitle).length === 0
    ) {
      setMessage('Se il match è titolato devi selezionare almeno un titolo');
      return;
    }

    await runWithBusy(editingMatchId ? 'Aggiornamento match...' : 'Creazione match...', async () => {
      try {
        let specialNotes = '';
        const selectedTitleValues = isTitleMatch
          ? normalizeMatchTitleValues(matchTitle, secondaryMatchTitle)
          : []
        const titleLabel = getMatchTitleLabel(
          { titles: selectedTitleValues },
          CHAMPIONSHIPS
        )

        if (winner === 'no_contest') {
          specialNotes = 'No-Contest';
        } else if (winner && isDisqualificationWin) {
          specialNotes = 'Vittoria per squalifica';
        }

        const finalNotes = specialNotes;

        const allParticipantIds = isMultiTeamMatch ? teams.flatMap(team => team.participants) : matchParticipants;

        const referencedGuestIds = Array.from(new Set(
          allParticipantIds.filter(id => typeof id === 'string' && id.startsWith('guest_'))
        ));
        const guests = referencedGuestIds.map(id => ({ id, ...(guestMap[id] || {}) }));

        const matchData = {
          participants: allParticipantIds,
          guests: guests.length > 0 ? guests : null,
          stipulation: matchStipulation || null,
          isTitleMatch,
          title: selectedTitleValues[0] || null,
          secondaryTitle: selectedTitleValues[1] || null,
          titles: selectedTitleValues,
          isMultiTeam: isMultiTeamMatch,
          winner: winner && winner !== 'no_contest' ? winner : null,
          notes: finalNotes || null,
          timestamp: new Date()
        };

        if (isMultiTeamMatch) {
          matchData.teams = teams.map(team => ({
            id: team.id,
            name: getTeamNameFromParticipants(team.participants, team.name),
            participants: team.participants,
            color: team.color
          }));
        }

        if (editingMatchId) {
          const previousMatch = matches.find(item => item.id === editingMatchId);
          await updateDoc(doc(db, 'shows', showId, 'matches', editingMatchId), matchData);
          await recordAdminUpdate({
            type: 'Match',
            action: 'Modifica',
            title: showName || showId,
            description: buildChangeDescription('Match modificato', previousMatch, matchData, [
              {
                key: 'participants',
                label: 'partecipanti',
                format: value => Array.isArray(value) ? `${value.length} partecipanti` : '0 partecipanti'
              },
              { key: 'stipulation', label: 'stipulazione' },
              {
                key: 'isTitleMatch',
                label: 'match titolato',
                format: value => value ? 'Si' : 'No'
              },
              { key: 'title', label: 'titolo' },
              { key: 'secondaryTitle', label: 'secondo titolo' },
              { key: 'winner', label: 'vincitore' },
              { key: 'notes', label: 'note' },
              {
                key: 'isMultiTeam',
                label: 'multi-team',
                format: value => value ? 'Si' : 'No'
              }
            ]),
            entityType: 'shows',
            entityId: showId
          });
          setMessage('Match aggiornato con successo');
        } else {
          const matchRef = await addDoc(collection(db, 'shows', showId, 'matches'), matchData);
          await recordAdminUpdate({
            type: 'Match',
            action: 'Creazione',
            title: showName || showId,
            description: `Match creato: ${matchStipulation || 'match'}${titleLabel ? ` (${titleLabel})` : ''}`,
            entityType: 'shows',
            entityId: showId,
            metadata: { matchId: matchRef.id }
          });
          setMessage('Match creato con successo');
        }

        const matchesRef = collection(db, 'shows', showId, 'matches');
        const matchesSnap = await getDocs(matchesRef);
        const matchesData = await Promise.all(
          matchesSnap.docs.map(async (matchDoc) => {
            const data = matchDoc.data();

            const participantIds = Array.isArray(data.participants) ? data.participants : [];
            const participantsDetails = await Promise.all(
              participantIds.map(async (wrestlerId) => {
                if (typeof wrestlerId === 'string' && wrestlerId.startsWith('guest_')) {
                  const guestEntry = Array.isArray(data.guests) ? data.guests.find(g => g.id === wrestlerId) : null;
                  return {
                    id: wrestlerId,
                    name: guestEntry?.name || 'Ospite',
                    isGuest: true,
                    imageUrl: guestEntry?.imageUrl || null
                  };
                }
                const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
                const wrestlerSnap = await getDoc(wrestlerRef);
                return {
                  id: wrestlerId,
                  name: wrestlerSnap?.data()?.name || 'Sconosciuto'
                };
              })
            );

            const teamsDetails = Array.isArray(data.teams) ? await Promise.all(
              data.teams.map(async (team) => {
                const teamParticipantIds = Array.isArray(team.participants) ? team.participants : [];
                const teamParticipantsDetails = await Promise.all(
                  teamParticipantIds.map(async (wrestlerId) => {
                    if (typeof wrestlerId === 'string' && wrestlerId.startsWith('guest_')) {
                      const guestEntry = Array.isArray(data.guests) ? data.guests.find(g => g.id === wrestlerId) : null;
                      return {
                        id: wrestlerId,
                        name: guestEntry?.name || 'Ospite',
                        isGuest: true,
                        imageUrl: guestEntry?.imageUrl || null
                      };
                    }
                    const wrestlerRef = doc(db, 'wrestlers', wrestlerId);
                    const wrestlerSnap = await getDoc(wrestlerRef);
                    return {
                      id: wrestlerId,
                      name: wrestlerSnap?.data()?.name || 'Sconosciuto'
                    };
                  })
                );
                return {
                  ...team,
                  participantsDetails: teamParticipantsDetails
                };
              })
            ) : [];

            return {
              id: matchDoc.id,
              ...data,
              participantsDetails,
              teamsDetails
            };
          })
        );
        setMatches(matchesData);

        setMatchParticipants([]);
        setMatchStipulation('');
        setMatchTitle('');
        setSecondaryMatchTitle('');
        setIsTitleMatch(false);
        setIsMultiTeamMatch(false);
        setTeams([{ id: 1, name: getDefaultTeamName(1), participants: [], color: 'danger' }]);
        setWinner('');
        setEditingMatchId(null);
        setIsDisqualificationWin(false);
        setGuestMap({});

      } catch (error) {
        console.error('Errore salvataggio match:', error);
        setMessage('Errore durante il salvataggio del match');
      }
    });
  };

  const handleEditMatch = (match) => {
    const nextTitleValues = getMatchTitleValues(match);
    setMatchParticipants(match.participants);
    setMatchStipulation(match.stipulation || '');
    setMatchTitle(nextTitleValues[0] || '');
    setSecondaryMatchTitle(nextTitleValues[1] || '');
    setIsTitleMatch(isStoredTitleMatch(match));
    setIsMultiTeamMatch(match.isMultiTeam || false);
    setTeams(match.teams || [{ id: 1, name: getDefaultTeamName(1), participants: [], color: 'danger' }]);
    setWinner(match.winner || '');
    setEditingMatchId(match.id);
    if (Array.isArray(match.guests) && match.guests.length > 0) {
      const gm = {};
      match.guests.forEach(g => {
        if (g && g.id) gm[g.id] = { id: g.id, name: g.name, imageUrl: g.imageUrl || null };
      });
      setGuestMap(gm);
    } else {
      setGuestMap({});
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo match?')) return;

    await runWithBusy('Eliminazione match...', async () => {
      try {
        const match = matches.find(item => item.id === matchId);
        const titleLabel = getMatchTitleLabel(match, CHAMPIONSHIPS);
        await deleteDoc(doc(db, 'shows', showId, 'matches', matchId));
        await recordAdminUpdate({
          type: 'Match',
          action: 'Eliminazione',
          title: showName || showId,
          description: `Match eliminato: ${match?.stipulation || 'match'}${titleLabel ? ` (${titleLabel})` : ''}`,
          entityType: 'shows',
          entityId: showId,
          metadata: { matchId }
        });
        setMatches(prev => prev.filter(m => m.id !== matchId));
        setMessage('Match eliminato con successo');
      } catch (error) {
        console.error('Errore eliminazione match:', error);
        setMessage('Errore durante l\'eliminazione del match');
      }
    });
  };

  const handleCancelMatchEdit = () => {
    setMatchParticipants([]);
    setMatchStipulation('');
    setMatchTitle('');
    setSecondaryMatchTitle('');
    setIsTitleMatch(false);
    setIsMultiTeamMatch(false);
    setTeams([{ id: 1, name: getDefaultTeamName(1), participants: [], color: 'danger' }]);
    setWinner('');
    setEditingMatchId(null);
    setGuestMap({});
  };

  const addTeam = () => {
    if (teams.length < 6) {
      const newTeamId = teams.length + 1;
      const newColor = TEAM_COLORS[teams.length % TEAM_COLORS.length];
      setTeams([...teams, {
        id: newTeamId,
        name: getDefaultTeamName(newTeamId),
        participants: [],
        color: newColor
      }]);
    }
  };

  const addSavedTeamToMatch = (savedTeam) => {
    const availableIds = new Set(availableWrestlersForMatches.map(wrestler => wrestler.id));
    const teamMembers = (savedTeam?.members || []).filter(memberId => availableIds.has(memberId));
    if (teamMembers.length === 0) {
      setMessage('Nessun membro di questo team e presente nelle partecipazioni dello show');
      return;
    }

    if (!isMultiTeamMatch) {
      setMatchParticipants(prev => [...new Set([...prev, ...teamMembers])]);
      return;
    }

    if (teams.length >= 6 && !teams.some(team => team.participants.length === 0)) {
      setMessage('Puoi creare al massimo 6 squadre per match');
      return;
    }

    const assignedIds = new Set(teams.flatMap(team => team.participants));
    const availableTeamMembers = teamMembers.filter(memberId => !assignedIds.has(memberId));
    if (availableTeamMembers.length === 0) {
      setMessage('Tutti i membri disponibili di questo team sono gia assegnati a una squadra');
      return;
    }

    setMatchParticipants(prev => prev.filter(id => !availableTeamMembers.includes(id)));
    setTeams(prev => {
      const emptyTeam = prev.find(team => team.participants.length === 0);
      if (emptyTeam) {
        return prev.map(team => (
          team.id === emptyTeam.id
            ? withAutoTeamName({ ...team, participants: availableTeamMembers }, savedTeam.name)
            : team
        ));
      }

      const newTeamId = Math.max(...prev.map(team => team.id), 0) + 1;
      const newColor = TEAM_COLORS[prev.length % TEAM_COLORS.length];
      return [
        ...prev,
        {
          id: newTeamId,
          name: getTeamNameFromParticipants(availableTeamMembers, savedTeam.name),
          participants: availableTeamMembers,
          color: newColor
        }
      ];
    });
  };

  const removeTeam = (teamId) => {
    if (teams.length > 1) {
      const teamToRemove = teams.find(t => t.id === teamId);
      setMatchParticipants(prev => [...prev, ...teamToRemove.participants]);
      setTeams(teams.filter(t => t.id !== teamId));

      if (winner === `team${teamId}`) {
        setWinner('');
      }
    }
  };

  const addToTeam = (teamId, wrestlerId) => {
    setTeams(teams.map(team => {
      if (team.id === teamId && !team.participants.includes(wrestlerId)) {
        return withAutoTeamName({
          ...team,
          participants: [...team.participants, wrestlerId]
        });
      }
      return withAutoTeamName(team);
    }));
    setMatchParticipants(prev => prev.filter(id => id !== wrestlerId));
  };

  const removeFromTeam = (teamId, wrestlerId) => {
    setTeams(teams.map(team => {
      if (team.id === teamId) {
        return withAutoTeamName({
          ...team,
          participants: team.participants.filter(id => id !== wrestlerId)
        });
      }
      return withAutoTeamName(team);
    }));
    setMatchParticipants(prev => [...prev, wrestlerId]);
  };

  const moveToTeam = (fromTeamId, toTeamId, wrestlerId) => {
    setTeams(teams.map(team => {
      if (team.id === fromTeamId) {
        return withAutoTeamName({
          ...team,
          participants: team.participants.filter(id => id !== wrestlerId)
        });
      }
      if (team.id === toTeamId && !team.participants.includes(wrestlerId)) {
        return withAutoTeamName({
          ...team,
          participants: [...team.participants, wrestlerId]
        });
      }
      return withAutoTeamName(team);
    }));
  };

  const addGuest = (gid, name, imageUrl) => {
    setGuestMap(prev => ({ ...prev, [gid]: { id: gid, name, imageUrl } }));
  };

  const addParticipant = (participantId) => {
    if (!matchParticipants.includes(participantId)) {
      setMatchParticipants([...matchParticipants, participantId]);
    }
  };

  const removeParticipant = (wrestlerId, guest) => {
    setMatchParticipants(matchParticipants.filter(id => id !== wrestlerId));
    if (guest) {
      setGuestMap(prev => {
        const next = { ...prev };
        delete next[wrestlerId];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="edit-show dark-theme show-editor-loading-page">
        <ShowEditorProgress
          label={busyLabel || 'Caricamento dati...'}
          value={busyProgress}
          fullPage
        />
      </div>
    );
  }

  if (!showData) {
    return (
      <div className="container-fluid py-4 alert alert-danger text-center">
        Impossibile caricare i dati dello show
      </div>
    );
  }

  if (!getShowBonusCategory(showData)) {
    return (
      <div className="container-fluid py-4 alert alert-danger text-center">
        Tipo show non valido: {showData.type || 'non specificato'}
      </div>
    );
  }

  const autoPublishTargetDate = resolveAutoPublishTargetDate(
    showData?.data,
    showData?.autoPublishDraftsTargetAt
  );
  const autoPublishNoonLabel = formatAutoPublishTargetLabel(autoPublishTargetDate);
  const autoPublishHandled = Boolean(showData?.autoPublishDraftsHandledForDate);
  const autoPublishLastAttemptLabel = formatTimestampLabel(showData?.autoPublishDraftsLastAttemptAt);
  const autoPublishLastPublishedLabel = formatTimestampLabel(showData?.autoPublishDraftsLastPublishedAt);
  const autoPublishLastPublishedCount = Number(showData?.autoPublishDraftsLastPublishedCount) || 0;
  const autoPublishStatusLabel = autoPublishHandled
    ? 'Autopubblicazione completata per questo show.'
    : autoPublishCountdown.isReady
    ? 'Orario raggiunto: in attesa del prossimo controllo automatico del server.'
    : 'Autopubblicazione programmata sul server.';
  const showAutoPublishCountdown = autoPublishDraftsAtNoon && !autoPublishHandled && autoPublishTargetDate;
  const autoPublishCountdownItems = [
    { label: 'giorni', value: autoPublishCountdown.days },
    { label: 'ore', value: autoPublishCountdown.hours },
    { label: 'min', value: autoPublishCountdown.minutes },
    { label: 'sec', value: autoPublishCountdown.seconds }
  ];

  const darkThemeStyles = `
    .edit-show.dark-theme {
      background-color: #121212;
      color: #e0e0e0;
    }
    .edit-show.dark-theme .card,
    .edit-show.dark-theme .card-header,
    .edit-show.dark-theme .card-footer,
    .edit-show.dark-theme .list-group-item,
    .edit-show.dark-theme .modal-content,
    .edit-show.dark-theme .dropdown-menu {
      background-color: #1e1e1e;
      border-color: #333;
      color: #e0e0e0;
    }
    .edit-show.dark-theme .card-header {
      border-bottom-color: #333;
    }
    .edit-show.dark-theme .form-control,
    .edit-show.dark-theme .form-select,
    .edit-show.dark-theme .input-group-text {
      background-color: #2c2c2c;
      border-color: #444;
      color: #f0f0f0;
    }
    .edit-show.dark-theme .form-control:focus,
    .edit-show.dark-theme .form-select:focus {
      background-color: #333;
      color: #fff;
      border-color: #0d6efd;
      box-shadow: 0 0 0 0.25rem rgba(13,110,253,0.25);
    }
    .edit-show.dark-theme .btn-outline-secondary {
      color: #ccc;
      border-color: #555;
    }
    .edit-show.dark-theme .btn-outline-secondary:hover {
      background-color: #444;
      color: #fff;
    }
    .edit-show.dark-theme .nav-tabs .nav-link {
      background-color: #1e1e1e;
      border-color: #333;
      color: #ccc;
    }
    .edit-show.dark-theme .nav-tabs .nav-link.active {
      background-color: #0d6efd;
      border-color: #0d6efd;
      color: white;
    }
    .edit-show.dark-theme .table {
      color: #e0e0e0;
    }
    .edit-show.dark-theme .table-striped > tbody > tr:nth-of-type(odd) > * {
      background-color: rgba(255,255,255,0.05);
    }
    .edit-show.dark-theme .bg-light {
      background-color: #2c2c2c !important;
    }
    .edit-show.dark-theme .text-muted {
      color: #aaa !important;
    }
    .edit-show.dark-theme .border {
      border-color: #444 !important;
    }
    @media (max-width: 768px) {
      .edit-show.dark-theme .btn {
        padding: 10px 12px;
        font-size: 14px;
      }
      .edit-show.dark-theme .form-control,
      .edit-show.dark-theme .form-select {
        font-size: 16px;
        padding: 10px 12px;
      }
      .edit-show.dark-theme .nav-link {
        padding: 12px 8px;
        font-size: 14px;
      }
      .edit-show.dark-theme .card-body {
        padding: 1rem;
      }
      .edit-show.dark-theme .list-group-item {
        padding: 12px 10px;
      }
      .edit-show.dark-theme .form-check-input {
        width: 1.2em;
        height: 1.2em;
        margin-top: 0.1em;
      }
    }
  `;

  return (
    <ProtectedRoute>
      <div className="edit-show dark-theme container-fluid px-3 py-3">
        <style>{darkThemeStyles}</style>

        <div
          className="show-page__header position-relative mb-4 rounded overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${showData?.sfondo || ''})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: '160px',
            borderRadius: '12px'
          }}
        >
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
            <div className="container-fluid px-4">
              <div className="row">
                <div className="col-12">
                  <div className="d-flex flex-column align-items-center text-center">
                    <h1 className="h3 mb-2 fw-bold text-white">
                      <i className="bi bi-pencil-square me-2"></i>
                      {showName}
                    </h1>
                    <span className="badge bg-dark text-white rounded-pill fs-6 px-3 py-2">
                      {showDate}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="show-editor-settings-strip">
                  <div className={`show-editor-autopublish ${autoPublishDraftsAtNoon ? 'show-editor-autopublish--active' : ''}`}>
                    <div className="show-editor-autopublish__copy">
                      <strong>
                        <i className="bi bi-clock-history me-2"></i>
                        Pubblicazione draft automatica
                      </strong>
                      <small>
                        {autoPublishNoonLabel
                          ? `${autoPublishNoonLabel} il server pubblichera automaticamente le bozze, anche se chiudi la schermata.`
                          : "Le bozze verranno pubblicate automaticamente dal server all'orario programmato, anche se chiudi la schermata."}
                      </small>
                      {autoPublishDraftsAtNoon && (
                        <small className="show-editor-autopublish__status">
                          {autoPublishStatusLabel}
                        </small>
                      )}
                      {showAutoPublishCountdown && (
                        <div className="show-editor-autopublish__countdown" aria-label="Countdown pubblicazione automatica draft">
                          {autoPublishCountdownItems.map((item) => (
                            <span key={item.label} className="show-editor-autopublish__countdown-item">
                              <strong>{String(item.value).padStart(2, '0')}</strong>
                              <small>{item.label}</small>
                            </span>
                          ))}
                        </div>
                      )}
                      {autoPublishLastAttemptLabel && (
                        <small className="show-editor-autopublish__status">
                          Ultimo tentativo automatico: {autoPublishLastAttemptLabel}.
                        </small>
                      )}
                      {autoPublishLastPublishedLabel && (
                        <small className="show-editor-autopublish__status">
                          Ultima pubblicazione riuscita: {autoPublishLastPublishedLabel} ({autoPublishLastPublishedCount} bozze).
                        </small>
                      )}
                      {!autoPublishLastPublishedLabel && autoPublishHandled && (
                        <small className="show-editor-autopublish__status">
                          Nessuna bozza pubblicata nell'ultima esecuzione automatica.
                        </small>
                      )}
                    </div>
                    <div className="show-editor-autopublish__switch">
                      <button
                        className={`btn-toggle ${autoPublishDraftsAtNoon ? 'active' : 'inactive'}`}
                        onClick={() => handleToggleAutoPublishDrafts(!autoPublishDraftsAtNoon)}
                        type="button"
                      >
                        <span className="toggle-icon">
                          {autoPublishDraftsAtNoon ? '●' : '○'}
                        </span>
                        {autoPublishDraftsAtNoon ? 'ON' : 'OFF'}
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ minHeight: '68px' }}>
                  <div className="d-flex gap-2 border-bottom pb-2">
                    <button
                      type="button"
                      className={`btn btn-sm col-6 ${activeTab === 'participations' ? 'btn-light text-dark' : 'btn-secondary text-dark'}`}
                      onClick={() => setActiveTab('participations')}
                      style={{ transition: 'all 0.2s', borderRadius: '20px' }}
                    >
                      <i className="bi bi-people-fill me-1"></i>
                      Partecipazioni
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm col-6 ${activeTab === 'matches' ? 'btn-light text-dark' : 'btn-secondary text-dark'}`}
                      onClick={() => setActiveTab('matches')}
                      style={{ transition: 'all 0.2s', borderRadius: '20px' }}
                    >
                      <i className="bi bi-trophy-fill me-1"></i>
                      Match
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div className={`mt-3 alert alert-dismissible fade show ${message.includes('Errore') ? 'alert-danger' : 'alert-success'} shadow-sm`}>
            <div className="d-flex align-items-center">
              <i className={`bi ${message.includes('Errore') ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2 fs-5`}></i>
              <span className="flex-grow-1">{message}</span>
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage('')}
                aria-label="Chiudi"
              ></button>
            </div>
          </div>
        )}

        {busyCount > 0 || busyProgress > 0 ? (
          <div className="show-editor-busy-wrap">
            <ShowEditorProgress label={busyLabel} value={busyProgress} />
          </div>
        ) : null}

        {activeTab === 'participations' && (
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <ParticipationForm
                showData={showData}
                wrestlers={wrestlers}
                participations={activeParticipations}
                liveParticipations={participations}
                draftParticipations={draftParticipations}
                selectedWrestlers={selectedWrestlers}
                onWrestlerSelect={handleWrestlerSelection}
                onWrestlerRemove={handleWrestlerRemove}
                selectedBonuses={selectedBonuses}
                onBonusToggle={handleBonusToggle}
                onBonusQtyChange={handleQtyChange}
                totalPoints={totalPoints}
                onSubmit={handleSubmit}
                onCancel={handleCancelEdit}
                editingParticipationId={editingParticipationId}
                isBulkEdit={bulkEditMode}
                selectedParticipationsForEdit={selectedParticipationsForEdit}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                showPresets={showPresets}
                onTogglePresets={() => setShowPresets(v => !v)}
                selectedPreset={selectedPreset}
                onApplyPreset={applyPreset}
                savedTeams={savedTeams}
                participationMode={participationMode}
                onParticipationModeChange={setParticipationMode}
                showName={showName}
                isLiveBonusLocked={isLiveBonusLocked}
                liveBonusUnlockLabel={liveBonusUnlockLabel}
                bonusSuggestionsByWrestler={bonusSuggestionsByWrestler}
                bonusSuggestionsLoading={bonusSuggestionsLoading}
              />
            </div>

            <div className="col-12 col-lg-7">
              <ParticipationManager
                participations={activeParticipations}
                onEditParticipation={handleEditParticipation}
                onDeleteParticipation={handleDeleteParticipation}
                onParticipationSelect={handleParticipationSelection}
                selectedParticipationsForEdit={selectedParticipationsForEdit}
                editingParticipationId={editingParticipationId}
                onEnableBulkEdit={enableBulkEdit}
                participationMode={participationMode}
                draftCount={draftParticipations.length}
                liveCount={participations.length}
                isPublishingDrafts={publishingDrafts}
                onPublishDrafts={handlePublishDrafts}
                showData={showData}
                wrestlerScoresById={wrestlerScoresById}
                isLiveBonusLocked={isLiveBonusLocked}
                liveBonusUnlockLabel={liveBonusUnlockLabel}
                moveTargets={sameEventShows}
                moveTargetShowId={selectedMoveTargetShowId}
                onMoveTargetChange={setSelectedMoveTargetShowId}
                onMoveSelectedParticipations={handleMoveParticipationsToSiblingShow}
                isMovingParticipations={movingParticipations}
                bonusSuggestionsByWrestler={bonusSuggestionsByWrestler}
              />
            </div>
          </div>
        )}

        {activeTab === 'matches' && (
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <MatchForm
                availableWrestlersForMatches={availableWrestlersForMatches}
                matchParticipants={matchParticipants}
                onAddParticipant={addParticipant}
                onRemoveParticipant={removeParticipant}
                matchStipulation={matchStipulation}
                onStipulationChange={setMatchStipulation}
                isTitleMatch={isTitleMatch}
                onTitleMatchChange={setIsTitleMatch}
                matchTitle={matchTitle}
                onTitleChange={setMatchTitle}
                secondaryMatchTitle={secondaryMatchTitle}
                onSecondaryTitleChange={setSecondaryMatchTitle}
                isMultiTeamMatch={isMultiTeamMatch}
                onMultiTeamMatchChange={handleMultiTeamMatchChange}
                teams={teams}
                onAddTeam={addTeam}
                onRemoveTeam={removeTeam}
                onAddToTeam={addToTeam}
                onRemoveFromTeam={removeFromTeam}
                onMoveToTeam={moveToTeam}
                winner={winner}
                onWinnerChange={setWinner}
                isDisqualificationWin={isDisqualificationWin}
                onDisqualificationWinChange={setIsDisqualificationWin}
                guestMap={guestMap}
                onAddGuest={addGuest}
                savedGuests={savedGuests}
                savedTeams={savedTeams}
                onAddSavedTeam={addSavedTeamToMatch}
                onSubmit={handleCreateMatch}
                onCancel={handleCancelMatchEdit}
                editingMatchId={editingMatchId}
                showId={showId}
              />
            </div>

            <div className="col-12 col-lg-7">
              <MatchManager
                matches={matches}
                onEditMatch={handleEditMatch}
                onDeleteMatch={handleDeleteMatch}
                editingMatchId={editingMatchId}
              />
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
}

export default EditShow;
